import { FancyMultiSelect } from "@/shadcnComponents/ui/multiselect";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "@/shadcnComponents/ui/select";
import mixpanel from "mixpanel-browser";
import { Button } from "@/shadcnComponents/ui/button";

import {
  FieldAndPlaceHolderStyles,
  genderList,
  followerRangeList,
  collabWithBrandList,
  postFormatListV2,
  postFormatListForFixed,
} from "../Helpers/Helper";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/shadcnComponents/ui/card";
import { Label } from "@/shadcnComponents/ui/label";
import { Users, Target } from "lucide-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useNewCampaign } from "../CustomHooks/NewCampaignHooks";
import { useGeneralSettings } from "@/Providers/General";
import { useEffect } from "react";
import { MultiSelect } from "@/shadcnComponents/ui/multi-select";

export const InfluencerCriteriaForm = () => {
  const generalState = useGeneralSettings();
  const {
    formData,
    setFormData,
    masterData,
    selectedContentCategory,
    setSelectedContentCategory,
    selectedAudienceCategory,
    setSelectedAudienceCategory,
    selectedSeniority,
    setSelectedSeniority,
    selectedGender,
    setSelectedGender,
    handleSelectChange,
    handleDraftChange,
    handlePrevious,
    setOpenSelectPopup,
    setRefreshLoading,
    handleSaveData,

    selectedCities,
    setSelectedCities,
    selectedState,
    setSelectedState,
    selectedCountry,
    setSelectedAudienceLocationIds,
  } = useNewCampaign();

  const handleSave = async () => {
    setOpenSelectPopup(true);
    setRefreshLoading(true);
    await handleSaveData({
      isAddMore: false,
      isRefresh: true,
    });
    setRefreshLoading(false);
  };

  const getCityOptions = () => {
    if (selectedState.length === 0 || !masterData?.locationData) return [];

    const options: { value: string; label: string }[] = [];

    selectedState.forEach((stateId) => {
      const state = masterData?.locationData?.states?.find(
        (s) => s.stateId === stateId
      );
      if (state) {
        state.cities
          .filter((item) =>
            ["delhi", "new delhi", "goa"]?.includes(
              item.cityName?.toLowerCase()
            )
              ? true
              : item.cityName !== state?.stateName
          )
          .forEach((city) => {
            options.push({
              value: city.cityId,
              label: city.cityName,
            });
          });
      }
    });

    return options;
  };

  // Update the selection logic in useEffect
  useEffect(() => {
    let newSelectedLocationIds: string[] = [];

    if (!masterData?.locationData) {
      return;
    }

    // If no states selected, include all cities from all states
    if (selectedState.length === 0) {
      if (selectedCountry) {
        masterData?.locationData.states.forEach((state) => {
          state.cities.forEach((city) => {
            newSelectedLocationIds.push(city.cityId);
          });
        });
      }
    }
    // If states selected but no cities, include all cities from selected states
    else if (selectedState.length > 0 && selectedCities.length === 0) {
      selectedState.forEach((stateId) => {
        const state = masterData?.locationData.states.find(
          (s) => s.stateId === stateId
        );
        if (state) {
          state.cities.forEach((city) => {
            newSelectedLocationIds.push(city.cityId);
          });
        }
      });
    }
    // If specific cities are selected, include only those
    else {
      newSelectedLocationIds = selectedCities;
    }

    setSelectedAudienceLocationIds(newSelectedLocationIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, selectedState, selectedCities, masterData.locationData]);

  return (
    <>
      <div className="flex gap-[5%]">
        <div className="flex flex-col gap-4">
          <Card className="p-4">
            <CardHeader className="p-0 pb-4">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-blue-500" />
                <CardTitle className="text-sm font-semibold text-blue-500">
                  Influencer Criteria
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0 space-y-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium">No. of Followers</Label>
                <p className="text-xs text-gray-500">
                  Influencers will be shortlisted from within the range you set
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="min-followers">Minimum</Label>
                    <Select
                      name="minCreatorFollowers"
                      defaultValue={followerRangeList?.min[1]}
                      value={formData?.creatorInfo?.minCreatorFollowers}
                      onValueChange={(value) =>
                        handleSelectChange(
                          "minCreatorFollowers",
                          "creatorInfo",
                          value
                        )
                      }
                    >
                      <SelectTrigger className={FieldAndPlaceHolderStyles}>
                        <SelectValue placeholder="Minimum Followers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {followerRangeList?.min?.map((item) => (
                            <SelectItem key={item} value={item}>
                              {item}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max-followers">Maximum</Label>
                    <Select
                      name="maxCreatorFollowers"
                      defaultValue={followerRangeList?.max[0]}
                      value={formData?.creatorInfo?.maxCreatorFollowers}
                      onValueChange={(value) =>
                        handleSelectChange(
                          "maxCreatorFollowers",
                          "creatorInfo",
                          value
                        )
                      }
                    >
                      <SelectTrigger className={FieldAndPlaceHolderStyles}>
                        <SelectValue placeholder="Maximum Followers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {followerRangeList?.max?.map((item) => (
                            <SelectItem key={item} value={item}>
                              {item}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <p className="text-xs text-gray-500">
                    Mention the gender of the influencer(s) needed, If your
                    product is gender-specific
                  </p>
                  <FancyMultiSelect
                    className={FieldAndPlaceHolderStyles}
                    FRAMEWORKS={genderList?.map((item) => ({
                      value: item,
                      label: item,
                    }))}
                    placeholder={"Select Gender"}
                    value={selectedGender}
                    setValue={setSelectedGender}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="past-collaborations">
                    Past Collaborations
                  </Label>
                  <p className="text-xs text-gray-500">
                    Do you need Influencers who have worked with brands before?
                    Please specify
                  </p>
                  <Select
                    name="pastCollaboration"
                    defaultValue={formData?.creatorInfo?.isPastCollabs?.[0]}
                    value={formData?.creatorInfo?.isPastCollabs?.[0]}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        creatorInfo: {
                          ...formData.creatorInfo,
                          isPastCollabs: [value],
                        },
                      })
                    }
                  >
                    <SelectTrigger className={FieldAndPlaceHolderStyles}>
                      <SelectValue placeholder="Yes/No" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {collabWithBrandList?.map((item) => (
                          <SelectItem key={item} value={item}>
                            {item}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Post Format</Label>
                <p className="text-xs text-gray-500">
                  Select the format in which you want the collaborative post to
                  be shared
                </p>
                <div className="grid grid-cols-3 gap-4">
                  {(formData?.campaignInfo?.collabType === "fixed"
                    ? postFormatListForFixed
                    : postFormatListV2
                  )?.map((format) => (
                    <Button
                      key={format.k}
                      variant="outline"
                      className={`h-auto py-3 flex flex-col items-center justify-center px-4 ${
                        formData?.postInfo?.postDrafts?.filter(
                          (item) => item?.status === 1
                        )?.[0]?.postFormat === format?.k
                          ? "bg-[#F1F1F1] border-[#000000]"
                          : ""
                      }`}
                      onClick={() =>
                        handleDraftChange("postFormat", format?.k, 0)
                      }
                    >
                      <format.icon className="h-6 w-6" />
                      <span>{format.v}</span>
                      <span className="text-xs text-gray-500 text-wrap ">
                        {format.d}
                      </span>
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content-category">Content category</Label>
                <p className="text-xs text-gray-500">
                  Specify the kind of content your best-suited influencers
                  should be sharing regularly
                </p>
                <FancyMultiSelect
                  className={FieldAndPlaceHolderStyles}
                  FRAMEWORKS={masterData?.ContentCategory?.map((item) => ({
                    value: item?._id,
                    label: item?.categoryName,
                    description: item?.categoryDesc,
                  }))}
                  placeholder={"Select Content Category"}
                  value={selectedContentCategory}
                  setValue={setSelectedContentCategory}
                  limit={3}
                  onLimit={() =>
                    generalState?.showErrorToast(
                      "Limit Exceeded",
                      "You can select a maximum of 3 Content Categories"
                    )
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card className="p-4">
            <CardHeader className="p-0 pb-4">
              <div className="flex items-center space-x-2">
                <Target className="w-5 h-5 text-blue-500" />
                <CardTitle className="text-sm font-semibold text-blue-500">
                  Audience Criteria
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="job-roles">Job Roles</Label>
                  <p className="text-xs text-gray-500">
                    Select the kind of audience you want to reach for this
                    campaign
                  </p>
                  <FancyMultiSelect
                    className={FieldAndPlaceHolderStyles}
                    FRAMEWORKS={masterData?.AudienceType?.map((item) => ({
                      value: item?._id,
                      label: item?.LkdAudienceTypeName,
                      description: item?.LkdAudienceTypeDesc,
                    }))}
                    placeholder={"Select Audience"}
                    value={selectedAudienceCategory}
                    setValue={setSelectedAudienceCategory}
                    limit={3}
                    onLimit={() =>
                      generalState?.showErrorToast(
                        "Limit Exceeded",
                        "You can select a maximum of 3 Audience Categories"
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seniority">Seniority</Label>
                  <p className="text-xs text-gray-500">
                    Select the kind of audience you want to reach for this
                    campaign
                  </p>
                  <FancyMultiSelect
                    className={FieldAndPlaceHolderStyles}
                    FRAMEWORKS={masterData?.seniority}
                    placeholder={"Eg: Entry level"}
                    value={selectedSeniority}
                    setValue={setSelectedSeniority}
                    limit={3}
                    onLimit={() =>
                      generalState?.showErrorToast(
                        "Limit Exceeded",
                        "You can select a maximum of 3 Audience Categories"
                      )
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Location</Label>
                <p className="text-xs text-gray-500">
                  Select the kind of audience you want to reach for this
                  campaign
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Select disabled value={selectedCountry || "India"}>
                      <SelectTrigger id="country" className="w-full">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="India">India</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Currently only supporting India
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">State/Region</Label>
                    <MultiSelect
                      id="state"
                      placeholder="All States"
                      options={masterData?.locationData.states.map((state) => ({
                        value: state.stateId,
                        label: state.stateName,
                      }))}
                      value={selectedState}
                      onChange={setSelectedState}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <MultiSelect
                      id="city"
                      placeholder="All Cities"
                      options={getCityOptions()}
                      value={selectedCities}
                      onChange={setSelectedCities}
                      className="w-full"
                      disabled={selectedState.length === 0}
                    />
                  </div>
                </div>
              </div>

              {/* <div className="space-y-2">
                <Label>Location</Label>
                <p className="text-xs text-gray-500">
                  Select the kind of audience you want to reach for this
                  campaign
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us">United States</SelectItem>
                      <SelectItem value="uk">United Kingdom</SelectItem>
                      <SelectItem value="ca">Canada</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ca">California</SelectItem>
                      <SelectItem value="ny">New York</SelectItem>
                      <SelectItem value="tx">Texas</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="City" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sf">San Francisco</SelectItem>
                      <SelectItem value="la">Los Angeles</SelectItem>
                      <SelectItem value="sd">San Diego</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div> */}
            </CardContent>
          </Card>
          <div className="mt-6 flex justify-between">
            <Button
              onClick={() => {
                mixpanel.track(
                  "Brand Draft Campaign - Influencer Criteria Section: Back - clicked"
                );
                handlePrevious();
              }}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              onClick={(e) => {
                mixpanel.track(
                  "Brand Draft Campaign - Influencer Criteria Section: Save and Continue - clicked"
                );
                e.preventDefault();
                handleSave();
                // handleNext();
              }}
              className="flex items-center gap-2"
              style={{ backgroundColor: "#DC2627" }}
            >
              Save & Continue
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
